export function ProcessServerErrors(serverErrors: Record<string, string[]>): Record<string, string> {
	return Object.fromEntries(
		Object.entries(serverErrors).map(([key, messages]) => [key, messages[0] ?? ""])
	);
}

/**
 * The relevant parts of a failed server request, extracted from an unknown caught value.
 * Each field is null when the caught value does not carry it.
 */
export interface ParsedServerError {
	status: number | null,
	fieldErrors: Record<string, string[]> | null,
	serverMessage: string | null,
	exceptionMessage: string | null,
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

/**
 * Narrows an unknown caught error (usually an axios error) into the parts our
 * handlers use: the HTTP status, the Laravel validation error bag, the server's
 * response message, and the underlying Error message. Using this keeps `catch`
 * variables typed as `unknown` instead of `any`.
 */
export function ParseServerError(e: unknown): ParsedServerError {
	const parsed: ParsedServerError = {
		status: null,
		fieldErrors: null,
		serverMessage: null,
		exceptionMessage: null,
	};

	if (!isRecord(e)) {
		return parsed;
	}

	if (typeof e.message === "string") {
		parsed.exceptionMessage = e.message;
	}

	if (!isRecord(e.response)) {
		return parsed;
	}

	if (typeof e.response.status === "number") {
		parsed.status = e.response.status;
	}

	if (!isRecord(e.response.data)) {
		return parsed;
	}

	if (typeof e.response.data.message === "string") {
		parsed.serverMessage = e.response.data.message;
	}

	if (isRecord(e.response.data.errors)) {
		parsed.fieldErrors = Object.fromEntries(
			Object.entries(e.response.data.errors).map(([key, val]) => [
				key,
				Array.isArray(val) ? val.map((item) => String(item)) : [String(val)],
			])
		);
	}

	return parsed;
}
